import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../config';
import { apiRequest } from '../../utils/api';
import { initialsAvatar } from '../../utils/avatar';

function Layout({ user, onLogin, onLogout }) {
  const [unreadInboxCount, setUnreadInboxCount] = useState(0);
  const [openDesktopDropdown, setOpenDesktopDropdown] = useState(null);
  const location = useLocation();
  const desktopNavRef = useRef(null);

  const discoverLinks = [
    { label: 'Events & Walks', to: '/activities', icon: 'event_available' },
    { label: 'Support', to: '/support', icon: 'health_and_safety' },
    { label: 'Resources', to: '/resources', icon: 'article' },
  ];

  const communityLinks = user
    ? [
      { label: 'Posts', to: '/community', icon: 'forum' },
      { label: 'Introductions', to: '/community/introductions', icon: 'waving_hand' },
      { label: 'MumsMarket', to: '/community/businesses', icon: 'storefront' },
    ]
    : [
      { label: 'MumsMarket', to: '/community/businesses', icon: 'storefront' },
    ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };
  
  const getLinkClass = (path) => {
    const base = "font-medium transition-colors text-sm";
    if (isActive(path)) {
      return `${base} text-primary`;
    }
    return `${base} text-stone-500 hover:text-primary`;
  };

  const getDropdownTriggerClass = (isActiveTrigger) => {
    const base = 'font-medium transition-colors text-sm inline-flex items-center gap-1';
    if (isActiveTrigger) {
      return `${base} text-primary`;
    }
    return `${base} text-stone-500 hover:text-primary`;
  };

  const isDiscoverActive = discoverLinks.some((item) => location.pathname.startsWith(item.to));
  const isCommunityActive = communityLinks.some((item) => location.pathname.startsWith(item.to));

  const getMobileNavClass = (path) => {
    return isActive(path)
      ? 'flex flex-col items-center justify-center text-primary px-1.5 py-1.5 relative min-w-0'
      : 'flex flex-col items-center justify-center text-stone-400 px-1.5 py-1.5 hover:text-primary transition-colors min-w-0';
  };

  const getIconStyle = (path) => {
    if (isActive(path)) {
      return { fontVariationSettings: "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24" };
    }
    return {};
  };

  const fetchUnreadInboxCount = useCallback(async () => {
    if (!user) {
      setUnreadInboxCount(0);
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        setUnreadInboxCount(0);
        return;
      }

      const data = await apiRequest('/messages-conversations', { token });
      const conversations = data?.conversations || [];
      const unread = conversations.reduce(
        (total, conversation) => total + (conversation.unread_count || 0),
        0
      );
      setUnreadInboxCount(unread);
    } catch (error) {
      console.error('Failed to fetch unread inbox count:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchUnreadInboxCount();

    if (!user) return;

    const intervalId = window.setInterval(fetchUnreadInboxCount, 90000);
    const handleFocus = () => fetchUnreadInboxCount();
    window.addEventListener('focus', handleFocus);

    const channel = supabase
      .channel(`layout-unread-${user.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'private_messages' },
        () => {
          fetchUnreadInboxCount();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversation_participants',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchUnreadInboxCount();
        }
      )
      .subscribe();

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      supabase.removeChannel(channel);
    };
  }, [user, fetchUnreadInboxCount, location.pathname]);

  useEffect(() => {
    setOpenDesktopDropdown(null);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!openDesktopDropdown) return;

    const handleOutsideClick = (event) => {
      if (desktopNavRef.current && !desktopNavRef.current.contains(event.target)) {
        setOpenDesktopDropdown(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpenDesktopDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openDesktopDropdown]);

  const renderDesktopDropdown = (items, dropdownKey) => (
    <AnimatePresence>
      {openDesktopDropdown === dropdownKey && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 6 }}
          transition={{ duration: 0.16 }}
          className="absolute top-full left-0 mt-3 w-64 rounded-2xl border border-outline-variant bg-white shadow-xl overflow-hidden"
        >
          <div className="py-2">
            {items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpenDesktopDropdown(null)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-surface-container-low transition-colors"
              >
                <span className="material-symbols-outlined text-[20px] text-stone-500">{item.icon}</span>
                <span className="text-sm font-medium text-stone-700">{item.label}</span>
                {item.badge && (
                  <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-secondary-container text-on-secondary-container">
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 bg-primary text-white px-4 py-2 rounded-lg">
        Skip to main content
      </a>
      
      {/* Header */}
      <header className="relative sticky top-0 z-sticky bg-surface/95 backdrop-blur-md border-b border-outline-variant">
        <nav className="px-4 md:px-6 py-2.5 md:py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center tracking-tight">
            <span className="font-script text-3xl md:text-5xl text-primary">Mums</span>
            <span className="font-headline italic text-lg md:text-2xl text-primary">Connect</span>
          </Link>
          
          {/* Desktop Nav */}
          <div
            ref={desktopNavRef}
            className="hidden md:flex items-center space-x-6 lg:space-x-8"
            onClickCapture={() => setOpenDesktopDropdown(null)}
          >
            <Link to="/" className={getLinkClass('/')}>Home</Link>

            <div
              className="relative"
              onMouseEnter={() => setOpenDesktopDropdown('discover')}
              onMouseLeave={() => setOpenDesktopDropdown((prev) => (prev === 'discover' ? null : prev))}
            >
              <Link to="/activities" className={getDropdownTriggerClass(isDiscoverActive)}>
                Discover
                <span className="material-symbols-outlined text-[18px]">expand_more</span>
              </Link>
              {renderDesktopDropdown(discoverLinks, 'discover')}
            </div>

            <div
              className="relative"
              onMouseEnter={() => setOpenDesktopDropdown('community')}
              onMouseLeave={() => setOpenDesktopDropdown((prev) => (prev === 'community' ? null : prev))}
            >
              <Link to={user ? '/community' : '/community/businesses'} className={getDropdownTriggerClass(isCommunityActive)}>
                Community
                <span className="material-symbols-outlined text-[18px]">expand_more</span>
              </Link>
              {renderDesktopDropdown(communityLinks, 'community')}
            </div>

            <Link to="/about" className={getLinkClass('/about')}>About</Link>
          </div>
          
          {/* Auth Section */}
          <div className="flex items-center gap-2 md:gap-3">
            {user ? (
              <div className="flex items-center gap-2">
                <Link to="/profile" className="flex items-center gap-2">
                  <span className="relative inline-flex">
                    <img 
                      src={user.avatar_url || user.user_metadata?.avatar_url || initialsAvatar(user.user_metadata?.full_name || user.user_metadata?.name || user.email, user.id)} 
                      alt="Profile" 
                      className="w-8 h-8 md:w-9 md:h-9 rounded-full object-cover border-2 border-primary-container shadow-sm"
                      onError={(e) => { 
                        e.target.src = initialsAvatar(user.user_metadata?.full_name || user.user_metadata?.name || user.email, user.id); 
                      }}
                    />
                    {unreadInboxCount > 0 && (
                      <span
                        className="absolute -top-1.5 -right-1 min-w-[16px] h-[16px] px-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold leading-none border border-white"
                        aria-label={`${unreadInboxCount} unread message${unreadInboxCount === 1 ? '' : 's'}`}
                      >
                        {unreadInboxCount > 9 ? '9+' : unreadInboxCount}
                      </span>
                    )}
                  </span>
                  <span className="hidden lg:block text-sm font-medium text-stone-600 max-w-[120px] truncate">
                    {user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0]}
                  </span>
                </Link>
                <button 
                  onClick={onLogout} 
                  className="hidden md:block text-sm text-stone-400 hover:text-stone-600 transition-colors"
                >
                  Logout
                </button>
              </div>
            ) : (
              <motion.button 
                whileTap={{ scale: 0.96 }}
                onClick={onLogin}
                className="bg-gradient-to-br from-primary to-primary-dim text-white px-5 py-2 rounded-full text-sm font-semibold shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
              >
                Sign In
              </motion.button>
            )}
            
          </div>
        </nav>
      </header>
      
      {/* Main Content */}
      <main id="main-content" className="flex-1 pb-20 md:pb-0">
        <Outlet />
      </main>
      
      {/* Footer - hidden on mobile (bottom nav covers that space) */}
      <footer className="hidden md:block bg-surface-container-low py-8 border-t border-outline-variant">
        <div className="px-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center tracking-tight">
            <span className="font-script text-3xl text-primary">Mums</span>
            <span className="font-headline italic text-lg text-primary">Connect</span>
          </div>
          <div className="flex flex-wrap justify-center gap-8 text-stone-500 text-sm">
            <Link to="/about" className="hover:text-primary transition-colors hover:underline">About</Link>
            <Link to="/privacy" className="hover:text-primary transition-colors hover:underline">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-primary transition-colors hover:underline">Terms</Link>
            <Link to="/data-deletion" className="hover:text-primary transition-colors hover:underline">Data Deletion</Link>
          </div>
          <div className="text-stone-400 text-xs">
            © {new Date().getFullYear()} MumsConnect Limited™
          </div>
        </div>
      </footer>
      
      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-sticky bg-white/90 backdrop-blur-xl border-t border-outline-variant flex justify-around items-center px-1.5 pb-[env(safe-area-inset-bottom,10px)] pt-2">
        <Link to="/" className={getMobileNavClass('/')}>
          <span className="material-symbols-outlined text-[24px]" style={getIconStyle('/')}>home</span>
          <span className="text-[11px] font-medium leading-none mt-1">Home</span>
          {isActive('/') && <span className="absolute -top-1 w-4 h-1 bg-primary rounded-full" />}
        </Link>
        <Link to="/activities" className={getMobileNavClass('/activities')}>
          <span className="material-symbols-outlined text-[24px]" style={getIconStyle('/activities')}>explore</span>
          <span className="text-[11px] font-medium leading-none mt-1">Discover</span>
          {isActive('/activities') && <span className="absolute -top-1 w-4 h-1 bg-primary rounded-full" />}
        </Link>
        {user ? (
          <Link to="/inbox" className={getMobileNavClass('/inbox')}>
            <span className="material-symbols-outlined text-[24px]" style={getIconStyle('/inbox')}>mail</span>
            <span className="text-[11px] font-medium leading-none mt-1">Inbox</span>
            {isActive('/inbox') && <span className="absolute -top-1 w-4 h-1 bg-primary rounded-full" />}
            {unreadInboxCount > 0 && (
              <span
                className="absolute top-0 right-1 min-w-[14px] h-[14px] px-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[8px] font-bold leading-none border border-white"
                aria-label={`${unreadInboxCount} unread message${unreadInboxCount === 1 ? '' : 's'}`}
              >
                {unreadInboxCount > 9 ? '9+' : unreadInboxCount}
              </span>
            )}
          </Link>
        ) : (
          <button onClick={onLogin} className="flex flex-col items-center justify-center text-stone-400 px-1.5 py-1.5 hover:text-primary transition-colors min-w-0">
            <span className="material-symbols-outlined text-[24px]">mail</span>
            <span className="text-[11px] font-medium leading-none mt-1">Inbox</span>
          </button>
        )}
        <Link to={user ? '/community' : '/community/businesses'} className={getMobileNavClass('/community')}>
          <span className="material-symbols-outlined text-[24px]" style={getIconStyle('/community')}>forum</span>
          <span className="text-[11px] font-medium leading-none mt-1">Community</span>
          {isActive('/community') && <span className="absolute -top-1 w-4 h-1 bg-primary rounded-full" />}
        </Link>
        <Link to="/menu" className={getMobileNavClass('/menu')}>
          <span className="material-symbols-outlined text-[24px]" style={getIconStyle('/menu')}>menu</span>
          <span className="text-[11px] font-medium leading-none mt-1">Menu</span>
          {isActive('/menu') && <span className="absolute -top-1 w-4 h-1 bg-primary rounded-full" />}
        </Link>
      </nav>
    </div>
  );
}

export default Layout;
